/**
 * MetadataHandler.ts
 * BATON handler for rich metadata fetching (artists, albums, tracks)
 * 
 * Follows the same pattern as ClientViewHandler.ts and ClientTransportHandler.ts
 * Fetches enriched metadata from the server and caches locally.
 * 
 * Commands: getArtistMetadata, getAlbumMetadata, getPersonMetadata
 * Events: artistMetadataLoaded, albumMetadataLoaded, personMetadataLoaded
 * 
 * Location: cloud (server fetches and relays)
 */

import {
  BatonMessage,
  CommandPayload,
  EventPayload,
  ResponsePayload,
  HandlerId,
  HandlerLocation,
  HandlerCapabilities,
  createCommand,
  validateMessage,
  PROTOCOL_VERSION,
} from '../protocols/BATON';

// =============================================================================
// TYPES
// =============================================================================

// -----------------------------------------------------------------------------
// Base Types
// -----------------------------------------------------------------------------

/** Artist metadata (base) */
export interface ArtistMetadata {
  id: string;
  name: string;
  imageUrl?: string;
  bio?: string;
  genres?: string[];
  followers?: number;
  albums?: AlbumMetadata[];
  topTracks?: TrackMetadata[];
  relatedArtists?: ArtistSummary[];
}

/** Album metadata */
export interface AlbumMetadata {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  imageUrl?: string;
  releaseDate?: string;
  year?: number;
  genre?: string;
  tracks?: TrackMetadata[];
  trackCount?: number;
  duration?: number;
}

/** Track metadata */
export interface TrackMetadata {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  album?: string;
  albumId?: string;
  imageUrl?: string;
  duration?: number;
  trackNumber?: number;
  streamUrl?: string;
}

// -----------------------------------------------------------------------------
// Enhanced Types for Rich Metadata (Roon-style)
// -----------------------------------------------------------------------------

/** Person role types */
export type PersonRole = 
  | 'band_member' 
  | 'producer' 
  | 'composer' 
  | 'engineer' 
  | 'session_musician'
  | 'orchestrator'
  | 'mixer'
  | 'mastering'
  | 'artist'
  | 'label_owner'
  | 'film_scorer'
  | 'pianist'
  | 'cellist'
  | 'software_developer';

/** Award/accolade */
export interface Award {
  id?: string;
  name: string;
  year: number;
  category?: string;
  work?: string;
}

/** Lightweight artist reference for links */
export interface ArtistSummary {
  id: string;
  name: string;
  relationship?: string;
  artworkUrl?: string;
}

/** Lightweight album reference for discographies */
export interface AlbumSummary {
  id: string;
  title: string;
  year?: number;
  artworkUrl?: string;
  type?: 'Album' | 'EP' | 'Single' | 'Compilation';
  role?: string;
  artistName?: string;
}

/** Band/artist member info */
export interface ArtistMember {
  personId: string;
  name: string;
  role: string;
  instruments?: string[];
  joinDate?: string;
  leaveDate?: string;
  isCurrent?: boolean;
}

/** Linked person reference */
export interface PersonReference {
  personId?: string;
  id?: string;
  name: string;
  role: string;
  projects?: string[];
}

/** Timeline event */
export interface TimelineEvent {
  year: number;
  event: string;
}

/** Production credit */
export interface ProductionCredit {
  albumId: string;
  albumTitle: string;
  artist: string;
  year: number;
  role: string;
}

/** Person metadata - for producers, composers, band members, etc. */
export interface PersonMetadata {
  id: string;
  name: string;
  type: 'person';
  roles: PersonRole[];
  
  imageUrl?: string;
  
  birthDate?: string;
  deathDate?: string;
  birthPlace?: string;
  currentLocation?: string;
  
  bio: string;           // Short summary (1-2 sentences)
  biography: string;     // Full narrative with [[linked names]]
  
  instruments?: string[];
  
  linkedArtists?: PersonReference[];
  collaboratedWith?: PersonReference[];
  
  soloDiscography?: AlbumSummary[];
  productionCredits?: ProductionCredit[];
  
  awards?: Award[];
  
  links?: Record<string, string>;
  
  source?: string;
  lastUpdated?: string;
}

/** Enhanced artist metadata with full Roon-style richness */
export interface EnhancedArtistMetadata extends ArtistMetadata {
  type?: 'solo_artist' | 'band' | 'orchestra' | 'ensemble';
  
  // Core info
  origin?: string;           // "Liverpool, England"
  formationDate?: string;    // "1960" 
  disbandDate?: string | null;
  isActive?: boolean;
  
  // Visual assets
  imageUrlHero?: string;     // Large banner image for detail view
  gallery?: string[];        // Additional images
  
  // Rich content
  bioSummary?: string;       // 2-3 sentence overview
  biographyNarrative?: string; // Long-form story with [[linked names]]
  
  // Members (for bands/orchestras)
  members?: ArtistMember[];
  formerMembers?: ArtistMember[];
  
  // Linked entities
  producers?: PersonReference[];
  collaborators?: PersonReference[];
  relatedArtists?: ArtistSummary[];
  
  // Statistics
  totalAlbums?: number;
  totalTracks?: number;
  totalPlays?: number;
  
  // Awards and recognition
  awards?: Award[];
  
  // Discography
  discography?: AlbumSummary[];
  
  // Timeline
  timeline?: TimelineEvent[];
  
  // External links
  links?: Record<string, string>;
  
  source?: string;
  lastUpdated?: string;
}

/** Enhanced album metadata with credits */
export interface EnhancedAlbumMetadata extends AlbumMetadata {
  description?: string;
  releaseType?: 'Album' | 'EP' | 'Single' | 'Compilation';
  label?: string;
  
  credits?: {
    producer?: string;
    mixing?: string;
    mastering?: string;
    [key: string]: string | undefined;
  };
  
  localCoverPath?: string;
}

/** Metadata event payload from server */
export interface MetadataEvent {
  event: 'artistMetadataLoaded' | 'albumMetadataLoaded' | 'trackMetadataLoaded' | 'personMetadataLoaded';
  artist?: EnhancedArtistMetadata;
  album?: AlbumMetadata;
  track?: TrackMetadata;
  person?: PersonMetadata;
  sourceDeviceId: string;
}

/** Callback for metadata events */
export type MetadataEventCallback = (event: MetadataEvent) => void;

/** Pending request for response handling */
interface PendingRequest {
  resolve: (data: any) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

// =============================================================================
// DEVICE ID HELPER
// =============================================================================

/**
 * Get or generate a persistent device ID
 * Reuses the same ID storage as other handlers for consistency
 */
function getDeviceId(): string {
  if (typeof localStorage !== 'undefined') {
    let id = localStorage.getItem('maestro_device_id');
    if (!id) {
      const platform = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios' : 'web';
      id = `${platform}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('maestro_device_id', id);
    }
    return id;
  }
  return `temp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
}

// =============================================================================
// METADATA HANDLER
// =============================================================================

export class MetadataHandler {
  readonly handlerId: HandlerId = 'metadata';
  readonly location: HandlerLocation = 'cloud';
  
  private ws: WebSocket | null = null;
  private eventListeners: Set<MetadataEventCallback> = new Set();
  private deviceId: string;
  
  // Pending requests awaiting server responses
  private pendingRequests: Map<string, PendingRequest> = new Map();
  
  // Local cache for metadata
  private artistCache: Map<string, EnhancedArtistMetadata> = new Map();
  private albumCache: Map<string, AlbumMetadata> = new Map();
  private personCache: Map<string, PersonMetadata> = new Map();
  
  // Request timeout (ms)
  private readonly REQUEST_TIMEOUT = 10000;
  
  constructor(deviceId?: string) {
    this.deviceId = deviceId || getDeviceId();
    try {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log(`[MetadataHandler] Initialized with deviceId: ${this.deviceId}`);
    } catch {}
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION
  // ---------------------------------------------------------------------------
  
  /**
   * Connect to WebSocket (called during app init)
   * Reuses the same WebSocket connection as other handlers
   */
  connect(ws: WebSocket): void {
    this.ws = ws;
    try {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log('[MetadataHandler] 🔌 WebSocket connected');
    } catch {}
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.ws = null;
    // Reject all pending requests
    this.pendingRequests.forEach((request, correlationId) => {
      clearTimeout(request.timeout);
      request.reject(new Error('WebSocket disconnected'));
    });
    this.pendingRequests.clear();
    try {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log('[MetadataHandler] 🔌 WebSocket disconnected');
    } catch {}
  }
  
  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
  
  // ---------------------------------------------------------------------------
  // SEMANTIC METHODS (UI calls these)
  // ---------------------------------------------------------------------------
  
  /**
   * Get artist metadata (enhanced with rich Roon-style data)
   * 
   * @param artistId - Artist ID
   * @param options - Optional { artistName } for fallback lookup
   * @returns Promise resolving to enhanced artist metadata
   * 
   * @example
   * const artist = await metadataHandler.getArtist('artist_broke_for_free', { artistName: 'Broke For Free' });
   */
  async getArtist(artistId: string, options?: { artistName?: string }): Promise<EnhancedArtistMetadata | null> {
    console.log(`[MetadataHandler] 🎤 getArtist: ${artistId}`, options);
    
    // Check cache first
    const cached = this.artistCache.get(artistId);
    if (cached) {
      console.log(`[MetadataHandler] Cache hit for artist: ${artistId}`);
      return cached;
    }
    
    // Send request to server
    const message = createCommand(this.handlerId, {
      cmd: 'getArtistMetadata',
      artistId,
      artistName: options?.artistName,
    } as CommandPayload);
    
    try {
      const response = await this.sendAndAwaitResponse(message);
      if (response.success && response.data) {
        const data = response.data as { artist?: EnhancedArtistMetadata };
        if (data.artist) {
          this.artistCache.set(artistId, data.artist);
          return data.artist;
        }
      }
      console.warn(`[MetadataHandler] Artist not found: ${artistId}`);
      return null;
    } catch (error) {
      console.error(`[MetadataHandler] Error fetching artist:`, error);
      return null;
    }
  }
  
  /**
   * Get person metadata (producers, composers, band members, etc.)
   * 
   * @param personId - Person ID
   * @param options - Optional { personName } for fallback lookup
   * @returns Promise resolving to person metadata
   * 
   * @example
   * const person = await metadataHandler.getPerson('person_george_martin', { personName: 'George Martin' });
   */
  async getPerson(personId: string, options?: { personName?: string }): Promise<PersonMetadata | null> {
    console.log(`[MetadataHandler] 👤 getPerson: ${personId}`, options);
    
    // Check cache first
    const cached = this.personCache.get(personId);
    if (cached) {
      console.log(`[MetadataHandler] Cache hit for person: ${personId}`);
      return cached;
    }
    
    // Send request to server
    const message = createCommand(this.handlerId, {
      cmd: 'getPersonMetadata',
      personId,
      personName: options?.personName,
    } as CommandPayload);
    
    try {
      const response = await this.sendAndAwaitResponse(message);
      if (response.success && response.data) {
        const data = response.data as { person?: PersonMetadata };
        if (data.person) {
          this.personCache.set(personId, data.person);
          return data.person;
        }
      }
      console.warn(`[MetadataHandler] Person not found: ${personId}`);
      return null;
    } catch (error) {
      console.error(`[MetadataHandler] Error fetching person:`, error);
      return null;
    }
  }
  
  /**
   * Get album metadata
   * 
   * @param albumId - Album ID
   * @param options - Optional { albumTitle } for fallback lookup
   * @returns Promise resolving to album metadata
   * 
   * @example
   * const album = await metadataHandler.getAlbum('456', { albumTitle: 'Dark Side of the Moon' });
   */
  async getAlbum(albumId: string, options?: { albumTitle?: string }): Promise<AlbumMetadata | null> {
    console.log(`[MetadataHandler] 💿 getAlbum: ${albumId}`, options);
    
    // Check cache first
    const cached = this.albumCache.get(albumId);
    if (cached) {
      console.log(`[MetadataHandler] Cache hit for album: ${albumId}`);
      return cached;
    }
    
    // Send request to server
    const message = createCommand(this.handlerId, {
      cmd: 'getAlbumMetadata',
      albumId,
      albumTitle: options?.albumTitle,
    } as CommandPayload);
    
    try {
      const response = await this.sendAndAwaitResponse(message);
      if (response.success && response.data) {
        const data = response.data as { album?: AlbumMetadata };
        if (data.album) {
          this.albumCache.set(albumId, data.album);
          return data.album;
        }
      }
      console.warn(`[MetadataHandler] Album not found: ${albumId}`);
      return null;
    } catch (error) {
      console.error(`[MetadataHandler] Error fetching album:`, error);
      return null;
    }
  }
  
  /**
   * Clear all cached metadata
   */
  clearCache(): void {
    this.artistCache.clear();
    this.albumCache.clear();
    this.personCache.clear();
    console.log('[MetadataHandler] Cache cleared');
  }
  
  // ---------------------------------------------------------------------------
  // EVENT HANDLING
  // ---------------------------------------------------------------------------
  
  /**
   * Called when WebSocket receives a metadata message from server
   * Routes to handleEvent for events, handleResponse for responses
   * 
   * @param message - Raw BATON message from WebSocket
   */
  handleMessage(message: BatonMessage | string): void {
    // Parse if string
    const msg: BatonMessage = typeof message === 'string' ? JSON.parse(message) : message;
    
    // Validate
    if (!validateMessage(msg)) {
      console.warn('[MetadataHandler] Invalid message format:', msg);
      return;
    }
    
    // Only handle messages for this handler
    if (msg.handler !== this.handlerId) {
      return;
    }
    
    // Handle responses (for our pending requests)
    if (msg.type === 'response') {
      this.handleResponse(msg);
      return;
    }
    
    // Handle events (metadata updates from other clients)
    if (msg.type === 'event') {
      const payload = msg.payload as MetadataEvent;
      this.handleEvent(payload);
    }
  }
  
  /**
   * Handle a response from the server
   */
  private handleResponse(message: BatonMessage): void {
    const messageId = message.id;
    if (!messageId) {
      console.warn('[MetadataHandler] Response without id:', message);
      return;
    }
    
    const pending = this.pendingRequests.get(messageId);
    if (!pending) {
      console.warn('[MetadataHandler] No pending request for id:', messageId);
      return;
    }
    
    // Clear timeout and remove from pending
    clearTimeout(pending.timeout);
    this.pendingRequests.delete(messageId);
    
    // Resolve the promise
    const payload = message.payload as ResponsePayload;
    pending.resolve(payload);
  }
  
  /**
   * Handle a metadata event from the server
   * Called when another client loads metadata (for cache sync)
   * 
   * @param event - The metadata event payload
   */
  handleEvent(event: MetadataEvent): void {
    // Don't process our own events
    if (event.sourceDeviceId === this.deviceId) {
      return;
    }
    
    console.log(`[MetadataHandler] 📥 Event received: ${event.event} from ${event.sourceDeviceId}`);
    
    // Update local cache from event
    if (event.event === 'artistMetadataLoaded' && event.artist) {
      this.artistCache.set(event.artist.id, event.artist);
    } else if (event.event === 'albumMetadataLoaded' && event.album) {
      this.albumCache.set(event.album.id, event.album);
    } else if (event.event === 'personMetadataLoaded' && event.person) {
      this.personCache.set(event.person.id, event.person);
    }
    
    // Notify all listeners
    this.eventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('[MetadataHandler] Event listener error:', err);
      }
    });
  }
  
  /**
   * Subscribe to metadata events
   * 
   * @param callback - Called when metadata events are received
   * @returns Unsubscribe function
   */
  onEvent(callback: MetadataEventCallback): () => void {
    this.eventListeners.add(callback);
    try {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log(`[MetadataHandler] Event subscriber added (total: ${this.eventListeners.size})`);
    } catch {}
    
    return () => {
      this.eventListeners.delete(callback);
      try {
        const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
        if (dbg) console.log(`[MetadataHandler] Event subscriber removed (total: ${this.eventListeners.size})`);
      } catch {}
    };
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL
  // ---------------------------------------------------------------------------
  
  /**
   * Send a BATON message and wait for the response
   */
  private sendAndAwaitResponse(message: BatonMessage): Promise<ResponsePayload> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }
      
      const messageId = message.id;
      if (!messageId) {
        reject(new Error('Message has no id'));
        return;
      }
      
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error(`Request timeout: ${messageId}`));
      }, this.REQUEST_TIMEOUT);
      
      // Store pending request
      this.pendingRequests.set(messageId, { resolve, reject, timeout });
      
      // Send message
      this.ws.send(JSON.stringify(message));
      console.log(`[MetadataHandler] 📤 Sent: ${(message.payload as any)?.cmd} (id: ${messageId})`);
    });
  }
  
  // ---------------------------------------------------------------------------
  // BATON HANDLER INTERFACE (Required methods)
  // ---------------------------------------------------------------------------
  
  /**
   * Initialize the handler
   */
  async init(): Promise<HandlerCapabilities> {
    return {
      handlerId: this.handlerId,
      version: '1.0.0',
      protocolVersion: PROTOCOL_VERSION,
      location: this.location,
      capabilities: {
        discovery: false,
        transport: [],
        volume: false,
        queue: false,
        gapless: false,
        grouping: false,
        search: false,
        authentication: false,
        offline: false,
      },
      deviceTypes: [],
      requirements: {
        subscription: 'none',
        networkAccess: 'any',
      },
    };
  }
  
  /**
   * Handle incoming command (client doesn't handle commands, only sends them)
   */
  async handleCommand(_message: BatonMessage<CommandPayload>): Promise<BatonMessage<ResponsePayload> | null> {
    // Client doesn't handle commands, only sends them
    return null;
  }
  
  /**
   * Shutdown the handler
   */
  async shutdown(): Promise<void> {
    console.log('[MetadataHandler] Shutting down...');
    this.eventListeners.clear();
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error('Handler shutting down'));
    });
    this.pendingRequests.clear();
    this.clearCache();
    this.ws = null;
    console.log('[MetadataHandler] Shutdown complete');
  }
  
  // ---------------------------------------------------------------------------
  // ACCESSORS
  // ---------------------------------------------------------------------------
  
  /**
   * Get this device's ID
   */
  getDeviceId(): string {
    return this.deviceId;
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let metadataHandlerInstance: MetadataHandler | null = null;

/**
 * Get the singleton MetadataHandler instance
 */
export function getMetadataHandler(): MetadataHandler {
  if (!metadataHandlerInstance) {
    metadataHandlerInstance = new MetadataHandler();
  }
  return metadataHandlerInstance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetMetadataHandler(): void {
  if (metadataHandlerInstance) {
    metadataHandlerInstance.shutdown();
    metadataHandlerInstance = null;
  }
}

// Export default instance for convenience
export const metadataHandler = getMetadataHandler();

